<script setup>
import { computed } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue'
import AppButton from '@/Components/AppButton.vue'
import { buildLinkAssetUrl } from '@/utils/assetUrls.js'

const props = defineProps({
  link: { type: Object, required: true },
  appearance: { type: Object, default: () => ({}) },
  buttonStyle: { type: [Object, Array, String], default: () => ({}) },
  DoBucket: String,
  DoEndpoint: String,
})

const emit = defineEmits(['click', 'share'])

const layout = computed(() => props.link?.options?.layout || 'classic')
const coverImageUrl = computed(() =>
  buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, props.link?.file),
)

function onClick(e){ e?.stopPropagation?.(); emit('click', props.link) }
function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }
</script>

<template>
  <!-- FEATURED layout -->
  <GeneralEmbedButton
    v-if="layout==='featured'"
    class="contact-form-card"
    :link="link"
    :appearance="appearance"
    :buttonStyle="buttonStyle"
    :DoBucket="DoBucket"
    :DoEndpoint="DoEndpoint"
    :layout="layout"
    @click="onClick"
  >
    <!-- Header: left icon, centered title, right share -->
    <div class="d-flex align-items-center mb-2" style="height:3rem; padding-top:6px; padding-left:12px; padding-right:12px; gap:8px;">
      <!-- Left Contact Form Icon -->
      <div class="d-flex align-items-center">
        <!-- Inline SVG icon (form) with configurable color -->
        <svg width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Contact form icon">
          <rect x="3" y="4" width="18" height="16" rx="2.5" :stroke="appearance.button_font_color || '#0f172a'" stroke-width="1.5" />
          <rect x="7" y="8" width="10" height="1.5" :fill="appearance.button_font_color || '#0f172a'"/>
          <rect x="7" y="12" width="7" height="1.5" :fill="appearance.button_font_color || '#0f172a'"/>
          <rect x="7" y="16" width="9" height="1.5" :fill="appearance.button_font_color || '#0f172a'"/>
        </svg>
      </div>
      <!-- Center Title -->
      <CardTitle :title="link.title || 'Contact form'" layout="classic" align="center" :max="100" class="flex-grow-1" style="min-width:0" />
      <!-- Share icon on the right -->
      <span class="d-flex align-items-center" aria-label="Share" style="flex-shrink:0">
        <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="SVGRepo_iconCarrier">
            <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                  d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
          </g>
        </svg>
      </span>
    </div>

    <!-- Visual area: show banner if file exists -->
    <div class="text-center mb-2">
      <div
        v-if="link?.file"
        class="mx-auto"
        :style="{
          width: '100%',
          height: '160px',
          borderRadius: '12px 12px 0 0',
          backgroundImage: `url(${coverImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }"
      ></div>
    </div>

    <!-- In-card action button -->
    <div class="text-center p-3" >
        <AppButton width="100%" :secondary="true" @click.stop="onClick"  :fontWeight="appearance.font_weight" :custom-class="appearance.buttons" :backgroundColor="appearance.button_color" :textColor="appearance.button_font_color" ignore-plan-restriction>Send message</AppButton>
    </div>
  </GeneralEmbedButton>

  <!-- CLASSIC layout -->
  <GeneralButton
    v-else
    :preventNav="true"
    class="contact-form-card"
    :link="link"
    :appearance="appearance"
    :buttonStyle="buttonStyle"
    :DoBucket="DoBucket"
    :DoEndpoint="DoEndpoint"
    :layout="layout"
    @click="onClick"
    @share="onShare"
  >
    <!-- Force left icon to contact-form icon when no file/platform icon desired -->
    <template #left>
      <div class="classic-icon d-flex align-items-center justify-content-center" :class="appearance.buttons" style="background:#f1f5f9">
        <!-- Inline SVG icon (form) with configurable color -->
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Contact form icon">
          <rect x="3" y="4" width="18" height="16" rx="2.5" :stroke="appearance.button_font_color || '#0f172a'" stroke-width="1.5" />
          <rect x="7" y="8" width="10" height="1.5" :fill="appearance.button_font_color || '#0f172a'"/>
          <rect x="7" y="12" width="7" height="1.5" :fill="appearance.button_font_color || '#0f172a'"/>
          <rect x="7" y="16" width="9" height="1.5" :fill="appearance.button_font_color || '#0f172a'"/>
        </svg>
      </div>
    </template>
    <template #title>
      <CardTitle :title="link.title || 'Contact form'" layout="classic" align="start" :max="100" />
    </template>
    <!-- Actions: use default unified share icon from GeneralButton if desired, or replicate -->
  </GeneralButton>
</template>

<style scoped>
.contact-form-card :deep(iframe){ border:0; }
</style>
